/**
 * Seção "Clínica e responsáveis" da etapa Complementos da CAT (#313).
 *
 * Tudo aqui é opcional: a pessoa que registra o acidente pela empresa muitas vezes não tem
 * permissão de ler clínicas nem sabe quem é o responsável. Por isso a seção nunca bloqueia o
 * formulário — sem opção para escolher (sem permissão, sem vínculo ou falha na busca), vira um
 * aviso calmo em fundo neutro e o cadastro segue.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  FormHelperText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import type { CatFormData, CatProviderOption } from '../../../types/cat';
import {
  gravadoDaClinica,
  gravadoDoResponsavel,
  ROTULO_CLINICA_FORA_DA_LISTA,
  ROTULO_RESPONSAVEL_FORA_DA_LISTA,
  type OpcoesDePrestadores,
  type PrestadoresGravados,
  type ValoresDePrestadores,
} from './catPrestadores';

const FONTE = 16;
const ALTURA_DE_TOQUE = 44;

interface CampoDeSelecaoProps {
  rotulo: string;
  valor: number | '' | undefined;
  opcoes: CatProviderOption[];
  gravado?: CatProviderOption;
  rotuloForaDaLista: string;
  ajuda?: string;
  disabled?: boolean;
  onChange: (valor: number | '') => void;
}

const CampoDeSelecao: React.FC<CampoDeSelecaoProps> = ({
  rotulo,
  valor,
  opcoes,
  gravado,
  rotuloForaDaLista,
  ajuda,
  disabled,
  onChange,
}) => {
  const idDoRotulo = useId();
  const idDaAjuda = useId();
  const escolhido = Number(valor);
  const naLista = opcoes.some((opcao) => opcao.id === escolhido);
  // O id gravado saiu das opções: a tela mostra o que o envio vai levar, com o nome da CAT ou um rótulo neutro.
  const foraDaLista: CatProviderOption | undefined =
    !!valor && !naLista
      ? { id: escolhido, nome: gravado?.id === escolhido ? gravado.nome : rotuloForaDaLista }
      : undefined;
  const lista = foraDaLista ? [...opcoes, foraDaLista] : opcoes;
  const valorVisivel = valor ? escolhido : '';

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const escolhido = event.target.value;
    onChange(escolhido === '' ? '' : Number(escolhido));
  };

  return (
    <Box>
      <Typography
        id={idDoRotulo}
        component="label"
        sx={{ display: 'block', fontSize: FONTE, fontWeight: 600, mb: 1 }}
      >
        {rotulo}
      </Typography>
      <Select<string | number>
        fullWidth
        displayEmpty
        labelId={idDoRotulo}
        value={valorVisivel}
        onChange={handleChange}
        disabled={disabled}
        aria-describedby={ajuda ? idDaAjuda : undefined}
        sx={{ fontSize: FONTE, minHeight: 48 }}
      >
        <MenuItem value="" sx={{ fontSize: FONTE, minHeight: ALTURA_DE_TOQUE }}>
          Nenhuma
        </MenuItem>
        {lista.map((opcao) => (
          <MenuItem key={opcao.id} value={opcao.id} sx={{ fontSize: FONTE, minHeight: ALTURA_DE_TOQUE }}>
            {opcao.nome}
          </MenuItem>
        ))}
      </Select>
      {ajuda && (
        <FormHelperText id={idDaAjuda} sx={{ fontSize: FONTE, mx: 0 }}>
          {ajuda}
        </FormHelperText>
      )}
    </Box>
  );
};

interface CatPrestadoresSectionProps extends OpcoesDePrestadores {
  companyId?: number;
  modo: 'create' | 'edit';
  valores: ValoresDePrestadores;
  gravados?: PrestadoresGravados;
  disabled?: boolean;
  onChange: (parcial: Partial<CatFormData>) => void;
}

const LIMPOS: ValoresDePrestadores = {
  clinicId: '',
  medicalResponsibleId: '',
  technicalResponsibleId: '',
  environmentalResponsibleId: '',
};

const temAlgum = (valores: ValoresDePrestadores): boolean =>
  Object.values(valores).some((valor) => !!valor);

export const CatPrestadoresSection: React.FC<CatPrestadoresSectionProps> = ({
  companyId,
  modo,
  estado,
  opcoes,
  valores,
  gravados,
  disabled,
  onChange,
}) => {
  const [aberto, setAberto] = useState(false);
  const [sugeridos, setSugeridos] = useState<Partial<ValoresDePrestadores>>({});
  const empresaAnterior = useRef(companyId);
  const sugeridoPara = useRef<number | undefined>(undefined);
  const idDoDetalhe = useId();

  // Trocou a empresa: os vínculos da anterior não valem para a nova.
  useEffect(() => {
    if (empresaAnterior.current !== undefined && empresaAnterior.current !== companyId) {
      setSugeridos({});
      if (temAlgum(valores)) onChange(LIMPOS);
    }
    empresaAnterior.current = companyId;
  }, [companyId, valores, onChange]);

  // Só no cadastro novo, uma vez por empresa, e só no campo ainda vazio. Escolher "Nenhuma"
  // depois disso não traz a sugestão de volta.
  useEffect(() => {
    if (modo !== 'create' || estado !== 'pronto' || companyId === undefined) return;
    if (sugeridoPara.current === companyId) return;
    sugeridoPara.current = companyId;

    const novos: Partial<ValoresDePrestadores> = {};
    if (!valores.clinicId && opcoes.clinicas.length === 1) novos.clinicId = opcoes.clinicas[0].id;
    if (!valores.medicalResponsibleId && opcoes.responsaveisMedicos.length === 1) {
      novos.medicalResponsibleId = opcoes.responsaveisMedicos[0].id;
    }
    if (Object.keys(novos).length > 0) {
      setSugeridos(novos);
      onChange(novos);
    }
  }, [modo, estado, companyId, opcoes, valores, onChange]);

  const gravadoClinica = gravadoDaClinica(gravados);
  const gravadoMedico = gravadoDoResponsavel(gravados?.medicalResponsible);
  const gravadoTecnico = gravadoDoResponsavel(gravados?.technicalResponsible);
  const gravadoAmbiental = gravadoDoResponsavel(gravados?.environmentalResponsible);

  const temOpcoes =
    estado === 'pronto' &&
    (opcoes.clinicas.length > 0 ||
      opcoes.responsaveisMedicos.length > 0 ||
      opcoes.responsaveisTecnicos.length > 0 ||
      opcoes.responsaveisAmbientais.length > 0);
  const temVinculo = temAlgum(valores);
  const detalheVisivel = aberto || !!valores.technicalResponsibleId || !!valores.environmentalResponsibleId;

  const sugerida = (campo: 'clinicId' | 'medicalResponsibleId'): boolean =>
    sugeridos[campo] !== undefined && valores[campo] === sugeridos[campo];

  const renderCorpo = () => {
    if (estado === 'carregando') {
      return (
        <Box
          role="status"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 96, fontSize: FONTE }}
        >
          <CircularProgress size={20} aria-hidden />
          Carregando clínicas e responsáveis...
        </Box>
      );
    }

    if (!temOpcoes && !temVinculo) {
      return (
        <Box
          role="note"
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 2,
            borderRadius: 1,
            bgcolor: 'grey.100',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <InfoIcon aria-hidden sx={{ color: 'text.secondary', mt: 0.25 }} />
          {estado === 'sem-empresa' ? (
            <Typography sx={{ fontSize: FONTE }}>
              Escolha a empresa na primeira etapa para ver as clínicas e responsáveis.
            </Typography>
          ) : (
            <Typography component="div" sx={{ fontSize: FONTE }}>
              <strong>Nenhuma clínica aparece para você escolher.</strong> Isso não impede de salvar. Quem
              gerencia a clínica pode informar clínica e responsável depois, na tela da CAT.
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Stack spacing={3}>
        <CampoDeSelecao
          rotulo="Clínica"
          valor={valores.clinicId}
          opcoes={opcoes.clinicas}
          gravado={gravadoClinica}
          rotuloForaDaLista={ROTULO_CLINICA_FORA_DA_LISTA}
          disabled={disabled}
          ajuda={sugerida('clinicId') ? 'Sugerida: é a única clínica vinculada a esta empresa.' : undefined}
          onChange={(valor) => onChange({ clinicId: valor })}
        />
        <CampoDeSelecao
          rotulo="Responsável médico"
          valor={valores.medicalResponsibleId}
          opcoes={opcoes.responsaveisMedicos}
          gravado={gravadoMedico}
          rotuloForaDaLista={ROTULO_RESPONSAVEL_FORA_DA_LISTA}
          disabled={disabled}
          ajuda={
            sugerida('medicalResponsibleId')
              ? 'Sugerida: é o único responsável médico desta empresa.'
              : undefined
          }
          onChange={(valor) => onChange({ medicalResponsibleId: valor })}
        />
        {!detalheVisivel && (
          <Box>
            <Button
              variant="text"
              aria-expanded={false}
              aria-controls={idDoDetalhe}
              onClick={() => setAberto(true)}
              sx={{ minHeight: ALTURA_DE_TOQUE, fontSize: FONTE, textTransform: 'none' }}
            >
              Informar também responsável técnico ou ambiental
            </Button>
          </Box>
        )}
        <Collapse in={detalheVisivel} unmountOnExit>
          <Stack spacing={3} id={idDoDetalhe}>
            <CampoDeSelecao
              rotulo="Responsável técnico"
              valor={valores.technicalResponsibleId}
              opcoes={opcoes.responsaveisTecnicos}
              gravado={gravadoTecnico}
              rotuloForaDaLista={ROTULO_RESPONSAVEL_FORA_DA_LISTA}
              disabled={disabled}
              onChange={(valor) => onChange({ technicalResponsibleId: valor })}
            />
            <CampoDeSelecao
              rotulo="Responsável ambiental"
              valor={valores.environmentalResponsibleId}
              opcoes={opcoes.responsaveisAmbientais}
              gravado={gravadoAmbiental}
              rotuloForaDaLista={ROTULO_RESPONSAVEL_FORA_DA_LISTA}
              disabled={disabled}
              onChange={(valor) => onChange({ environmentalResponsibleId: valor })}
            />
          </Stack>
        </Collapse>
      </Stack>
    );
  };

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Clínica e responsáveis{' '}
        <Typography component="span" sx={{ fontSize: FONTE, color: 'text.secondary', fontWeight: 400 }}>
          (opcional)
        </Typography>
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography sx={{ fontSize: FONTE, mb: 2 }}>
        Preencha se a clínica da empresa atendeu o acidente. Se não souber agora, pule: dá para completar
        depois.
      </Typography>
      {renderCorpo()}
    </>
  );
};
